import React, {useState, useEffect, useCallback, useMemo} from 'react';
import SMCModal from '../../components/core/SMCModal';
import SMCModalBody from '../../components/core/SMCModal/SMCModalBody'
import {useTranslation} from 'react-i18next';
import SMCModalButtonContainer from '../../components/core/SMCModal/SMCModalButtonContainer';
import SMCLabel from '../../components/Simple/SMCLabel';
import SMCButton from '../../components/Simple/SMCButton';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {useSMCErrorHandler} from '../../utils/errorUtils';
import EmptyRow from '../../components/Simple/EmptyRow';
import Api from '../../api/SMPFetchHelper';
import SMCPageLoading from '../../components/core/pageComponents/SMCPageLoading';
import {SMCRadioGroup, SMCRadio} from '../../components/Simple/SMCRadio';
import Indentation from '../../components/Simple/Indentation';
import { getAxiosFilename, enqueueFileDownload } from '../../utils/utils';

function GatewayManageCertificateModal({
    isOpen,
    onRequestClose,
    gatewayId,
    onSuccess
}) {
    const { t } = useTranslation();
    const { getErrorMessage } = useSMCErrorHandler();

    const [confirmed, setConfirmed] = useState(false);
    const [chosenOption, setChosenOption] = useState('REVOKE');
    const [done, setDone] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [certificateData, setCertificateData] = useState(null);
    const [certificateName, setCertificateName] = useState(null);

    const handleDismiss = useCallback(() => {
        onRequestClose();
    }, [onRequestClose]);

    const startExport = useCallback(async () => {
        try {
            if (chosenOption === 'REVOKE') {
                await Api.manageRevokeGatewayCertificate(gatewayId);
                setDone(true);
            }
            else if (chosenOption === 'EXPORT') {
                const resp = await Api.getGatewayCert(gatewayId);
                const fileName = getAxiosFilename(resp) || `${gatewayId}.crt`;
                setCertificateData(resp.data);
                setCertificateName(fileName);
                setDone(true);
            }
        } catch (e) {
            setHasError(true);
            const errorMessage = getErrorMessage(e);
            setErrorMessage(errorMessage);
        }
    }, [getErrorMessage, chosenOption, gatewayId]);

    // On each new open, reset values
    useEffect(() => {
        if (isOpen) {
            setConfirmed(false);
            setChosenOption('REVOKE');
            setDone(false);
            setHasError(false);
            setErrorMessage('');
            setCertificateData(null);
            setCertificateName(null);
        }
    }, [isOpen]);

    const body = useMemo(() => {
        if (!confirmed) {
            return (
                <>
                <SMCLabel bold fontSize="16px">{t('SMC_MANAGECERTWIZ_HEAD')}</SMCLabel>
                <EmptyRow />
                <SMCLabel>{t('SMC_MANAGECERTWIZ_TEXT')}</SMCLabel>
                <Indentation>
                    <SMCRadioGroup value={chosenOption} onChange={e => setChosenOption(e.target.value)}>
                        <SMCRadio value="REVOKE" label={t('SMC_MANAGECERTWIZ_REV')} />
                        <SMCRadio value="EXPORT" label={t('SMC_MANAGECERTWIZ_EXP')} />
                    </SMCRadioGroup>
                </Indentation>
                </>
            );
        }
        else if (hasError) {
            return (
                <>
                <SMCLabel bold fontSize="16px">{t('SMC_CAWIZ_DO_ERRMSG')}</SMCLabel>
                <EmptyRow />
                <SMCLabel>{errorMessage}</SMCLabel>
                <SMCLabel>{t('SMC_CAWIZ_DO_TRY_AGAIN')}</SMCLabel>
                </>
            )
        }
        else if (done) {
            // Done
            return (
                <>
                <SMCLabel bold fontSize="16px">{t('SMC_MANAGECERTDONEWIZ_HEAD')}</SMCLabel>
                <EmptyRow />
                {
                    chosenOption === 'REVOKE' && (
                        <SMCLabel>{t('SMC_MANAGECERTDONEWIZ_TEXT_REV')}</SMCLabel>
                    )
                }
                {
                    chosenOption === 'EXPORT' && (
                        <SMCLabel>{t('SMC_MANAGECERTDONEWIZ_TEXT_EXP')} <b>{t('SMC_MANAGECERTDONEWIZ_TEXT_EXP_SAVE_AS')}</b></SMCLabel>
                    )
                }
                </>
            )
        }
        else if (confirmed) {
            return <SMCPageLoading />;
        }
        else {
            return null;
        }
    }, [confirmed, chosenOption, done, errorMessage, hasError, t]);

    const buttons = useMemo(() => {
        if (!confirmed) {
            return (
                <>
                <SMCButton
                    onClick={() => {
                        setConfirmed(true);
                        startExport();
                    }}
                    style={{ marginRight: 5 }}
                >
                    {t('SMC_GEN_WIZNEXT')}
                    <FontAwesomeIcon style={{ marginTop: '3px', marginLeft: '5px' }} color="grey" icon="arrow-circle-right" />
                </SMCButton>
                <SMCButton onClick={handleDismiss}>
                    <FontAwesomeIcon style={{ marginTop: '3px', marginRight: '5px' }} color="grey" icon="times" />
                    {t('SMC_GEN_CANCEL')}
                </SMCButton> 
                </>
            );
        }
        else if (chosenOption === 'REVOKE') {
            return (
                <SMCButton onClick={handleDismiss}>
                    {t('SMC_GEN_DONE')}
                </SMCButton>
            );
        }
        else if (chosenOption === 'EXPORT') {
            return (
                <>
                <SMCButton onClick={() => enqueueFileDownload(certificateData, certificateName)}>
                    {t('SMC_SYSOPS_EXPWIZ_SAVEAS')}
                </SMCButton>
                <SMCButton onClick={handleDismiss}>
                    {t('SMC_GEN_DONE')}
                </SMCButton>
                </>
            );
        }
    }, [confirmed, startExport, chosenOption, handleDismiss, certificateData, certificateName, t]);

    return (
        <SMCModal
            isOpen={isOpen}
            width={710}
            height={480}
            title={t('SMC_MANAGECERTWIZ_TITLE')}
            onRequestClose={handleDismiss}
        >
            <SMCModalBody lineHeight="normal">
                { body }
            </SMCModalBody>
            <SMCModalButtonContainer>
                { buttons }
            </SMCModalButtonContainer>
        </SMCModal>

    );
}

export default GatewayManageCertificateModal;